/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { mapObjIndexed, prop } from 'ramda'
import React from 'react'

import { useColor } from '../../../../packages/kiti/components/theme/_hooks/colors'
import { applyStyle, getSmartSize } from '../../../../packages/kiti/utils/helpers/styles'
import { usePaddingStyle } from '../../../../packages/kiti/utils/styles/padding'
import { useGridStyle } from '../../../../packages/kiti/utils/styles/grid'
import { useSizeStyle } from '../../../../packages/kiti/utils/styles/size'
import Panel from '../../../../packages/kiti/components/structure/Panel'
import { Col, Grid } from '../../../../packages/kiti/components/structure/Grid'
import Separator from '../../../../packages/kiti/components/helpers/Separator'
import Line from '../../../../packages/kiti/components/helpers/Line'

export const Table = ({ children }) => {
  const style = css`
    display: table;
    width: 100%;
    border-collapse: collapse;
  `

  return (
    <div className="table" css={style}>
      {children}
    </div>
  )
}

const ROW_SIZES = {
  small: { head: 1.8, normal: 3, padding: 0.8 },
  normal: { head: 2, normal: 3.5, padding: 1 },
  medium: { head: 2.2, normal: 4, padding: 1.4 }
}

export const TableRow = ({
  children,
  disableRenderOnScreen,
  head,
  size,
  hasSidePadding,
  lineColor,
  showDeleted,
  ...props
}) => {
  const lineColorValue = useColor(lineColor || 'light3')
  const heightValue = getSmartSize(size, mapObjIndexed(prop(head ? 'head' : 'normal'), ROW_SIZES), 'normal')
  const sidePadding = getSmartSize(size, mapObjIndexed(prop('padding'), ROW_SIZES), 'normal')
  const style = css`
    width: 100%;
    display: table-row;
    align-items: center;
    ${applyStyle('height', heightValue)}
    ${applyStyle('border-bottom', !head, `solid 1px ${lineColorValue}`)}
    ${applyStyle('padding', head, '0.2rem 0')}
    ${applyStyle('background-color', head, '#f2f3f8')}
    ${applyStyle('padding-left', hasSidePadding, sidePadding)}
    ${applyStyle('padding-right', hasSidePadding, sidePadding)}
		${usePaddingStyle(props)}
  `

  return (
    <div className={`tableRow ${head ? 'head' : ''}`} css={style}>
      {children}
    </div>
  )
}

export const TableColText = ({ children, align, ...props }) => {
  const style = css`
    font-size: 0.625rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: 700;
    margin: 0;
    ${applyStyle('text-align', align || 'left')}
  `

  return (
    <p className="TableColText" css={style}>
      {children}
    </p>
  )
}

export const TableCol = ({ children, ...props }) => {
  const flex = props.width ? undefined : 1

  const style = css`
    ${useGridStyle({ flex, ...props })}
    ${useSizeStyle(props)}
    display: table-cell;
    vertical-align: middle;
    max-width: 10rem;
    padding: 0 0.5rem;
  `

  return (
    <div className="tableCol2" css={style}>
      {children}
    </div>
  )
}

export const TableContent = ({ inPanel, children }) => {
  if (!inPanel) return children
  return (
    <Panel noPadding bgColor="transparent">
      {children}
    </Panel>
  )
}

export const ExpandedTableRow = ({ children, smallPadding }) => {
  return (
    <TableRow disableRenderOnScreen>
      <Grid align="stretch" width="100%">
        <Separator vertical weight={smallPadding ? 3 : 8} />
        <Col>
          <Line height="100%" vertical color="light3" />
        </Col>
        <Separator vertical weight={5} />
        <Col flex={1}>{children}</Col>
      </Grid>
    </TableRow>
  )
}

export const DataTableWrapper = ({ children }) => {
  const style = css`
    max-width: 69rem;
    overflow-x: auto;
    overflow-y: hidden;

    &::-webkit-scrollbar {
      width: 10px;
      height: 10px;
    }

    &::-webkit-scrollbar-track {
      background-color: #f1f1f1;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: #c1c1c1;
      border-radius: 10px;
      border: 2px solid #f1f1f1;
    }

    &::-webkit-scrollbar-thumb:horizontal {
      background-color: #c1c1c1;
    }

    &::-webkit-scrollbar-thumb:vertical {
      background-color: #c1c1c1;
    }
  `
  return (
    <div className="tableWrapper" css={style}>
      {children}
    </div>
  )
}

export const DataTableColWrapper = ({ children, isFirstColumn, isLastColumn, head }) => {
  const style = css`
    position: sticky;
    ${applyStyle('width', isFirstColumn, '2rem')};
    ${applyStyle('width', isLastColumn, '3.5rem')};
    ${applyStyle('left', isFirstColumn, 0)};
    ${applyStyle('right', isLastColumn, 0)};
    background-color: ${head ? '#f2f3f8' : '#ffffff'};
    z-index: 10;
    min-height: ${head ? '1.8rem' : '3rem'};
    display: table-cell;
    vertical-align: middle;
  `
  return (
    <div className="tableColWrapper" css={style}>
      {children}
    </div>
  )
}
